import React, { useEffect, useState } from "react";
import { Loader2, Copy, Check, Cloud, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { PageHeader } from "@/components/admin/AdminUI";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

export default function AdminSalesforce() {
  const [cfg, setCfg] = useState(null);
  const [form, setForm] = useState({ client_id: "", client_secret: "", login_env: "production" });
  const [saving, setSaving] = useState(false);
  const [copied, setCopied] = useState(false);

  const load = () =>
    api.get("/connections/salesforce/config").then((r) => {
      setCfg(r.data);
      setForm((f) => ({ ...f, login_env: r.data.login_env || "production" }));
    }).catch(() => setCfg(false));

  useEffect(() => {
    load();
  }, []);

  const copyCallback = () => {
    navigator.clipboard.writeText(cfg.callback_url);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  const save = async () => {
    if (!form.client_id || !form.client_secret) {
      toast.error("Consumer Key and Secret are required");
      return;
    }
    setSaving(true);
    try {
      await api.put("/connections/salesforce/config", form);
      toast.success("Salesforce Connected App saved");
      setForm((f) => ({ ...f, client_secret: "" }));
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  if (cfg === null)
    return <div className="flex h-64 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>;
  if (cfg === false) return <p className="text-sm text-destructive">Failed to load Salesforce config.</p>;

  return (
    <div data-testid="admin-salesforce" className="max-w-2xl">
      <PageHeader title="Salesforce" subtitle="Connected App credentials for OAuth 2.0" />

      <div className="mb-6 flex items-center gap-3 rounded-xl border border-border bg-card p-4">
        <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10 text-primary">
          <Cloud className="h-5 w-5" />
        </div>
        <div className="flex-1">
          <p className="font-medium">Connected App status</p>
          <p className="text-sm text-muted-foreground">Environment: {cfg.login_env}</p>
        </div>
        <Badge variant={cfg.configured ? "default" : "secondary"} data-testid="sf-config-status">
          {cfg.configured ? "Configured" : "Not configured"}
        </Badge>
      </div>

      {/* Callback URL */}
      <div className="mb-6 rounded-xl border border-border bg-card p-5">
        <Label>Callback URL</Label>
        <p className="mb-3 mt-1 text-sm text-muted-foreground">
          Paste this exact URL into your Salesforce Connected App's <strong>Callback URL</strong> field, and enable
          scopes <code className="rounded bg-muted px-1 py-0.5 text-xs">api</code>,{" "}
          <code className="rounded bg-muted px-1 py-0.5 text-xs">refresh_token</code>,{" "}
          <code className="rounded bg-muted px-1 py-0.5 text-xs">offline_access</code>.
        </p>
        <div className="flex items-center gap-2">
          <Input readOnly value={cfg.callback_url} className="h-11 font-mono text-xs" data-testid="sf-callback-url" />
          <Button variant="outline" size="icon" className="h-11 w-11 shrink-0" onClick={copyCallback} data-testid="sf-copy-callback">
            {copied ? <Check className="h-4 w-4 text-primary" /> : <Copy className="h-4 w-4" />}
          </Button>
        </div>
      </div>

      {/* Credentials form */}
      <div className="space-y-4 rounded-xl border border-border bg-card p-5">
        <div className="space-y-2">
          <Label htmlFor="cid">Consumer Key (Client ID)</Label>
          <Input
            id="cid"
            value={form.client_id}
            onChange={(e) => setForm((f) => ({ ...f, client_id: e.target.value }))}
            placeholder={cfg.client_id_masked || "3MVG9..."}
            className="h-11 font-mono text-xs"
            data-testid="sf-client-id-input"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="csec">Consumer Secret</Label>
          <Input
            id="csec"
            type="password"
            value={form.client_secret}
            onChange={(e) => setForm((f) => ({ ...f, client_secret: e.target.value }))}
            placeholder="••••••••••••"
            className="h-11 font-mono text-xs"
            data-testid="sf-client-secret-input"
          />
        </div>
        <div className="space-y-2">
          <Label>Login environment</Label>
          <Select value={form.login_env} onValueChange={(v) => setForm((f) => ({ ...f, login_env: v }))}>
            <SelectTrigger data-testid="sf-env-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="production" data-testid="sf-env-production">Production (recommended)</SelectItem>
              <SelectItem value="sandbox" data-testid="sf-env-sandbox">Sandbox</SelectItem>
            </SelectContent>
          </Select>
          {form.login_env === "sandbox" && (
            <div className="flex items-start gap-2 rounded-lg border border-accent/40 bg-accent/10 px-3 py-2 text-xs text-accent" data-testid="sf-sandbox-warning">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
              We recommend connecting with Production. Only use Sandbox for testing against a Salesforce sandbox org.
            </div>
          )}
        </div>
        <Button onClick={save} disabled={saving} className="w-full font-semibold" data-testid="sf-save-config">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save Connected App"}
        </Button>
      </div>
    </div>
  );
}
